import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { supabase } from '../lib/supabase';
import { Booking, BookingTicketType, Movie, Screening } from '../types';
import { QRCodeSVG } from 'qrcode.react';
import { AlertTriangle, ShieldCheck, Eye, Mail, Trash2, Download, RefreshCw } from 'lucide-react';
import appLogo from '../../film-alt-svgrepo-com.svg';
import toast from 'react-hot-toast';
import WeddingBookings from './weddings/WeddingBookings';

interface ExtendedBooking extends Booking {
  movies: Movie;
  screenings: Screening;
  expires_at?: string;
  cancellation_reason?: string | null;
}

// Helper to check if a pending booking has expired
const isExpiredPending = (booking: ExtendedBooking): boolean => {
  if (booking.status !== 'pending') return false;
  if (!booking.expires_at) return false;
  return new Date(booking.expires_at) < new Date();
};

interface UserBookingsProps {
  onClose: () => void;
  onContinuePending?: (movie: Movie, screeningId: number, bookingId: number, seatIds: string[]) => Promise<void> | void;
}

export default function UserBookings({ onClose, onContinuePending }: UserBookingsProps) {
  const [bookings, setBookings] = useState<ExtendedBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedBookingId, setSelectedBookingId] = useState<number | null>(null);
  const [cancelingId, setCancelingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [continuingId, setContinuingId] = useState<number | null>(null);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
  const [userEmail, setUserEmail] = useState<string>('');
  const [seatIdsByBooking, setSeatIdsByBooking] = useState<Record<number, string[]>>({});
  const [ticketTypesByBooking, setTicketTypesByBooking] = useState<Record<number, BookingTicketType[]>>({});
  const [qrValueByBooking, setQrValueByBooking] = useState<Record<number, string>>({});
  const [qrRedeemedByBooking, setQrRedeemedByBooking] = useState<Record<number, boolean>>({});
  const [feedbackOpenFor, setFeedbackOpenFor] = useState<number | null>(null);
  const [feedbackSubject, setFeedbackSubject] = useState('');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [sendingFeedback, setSendingFeedback] = useState(false);
  const [pdfPreviewFor, setPdfPreviewFor] = useState<number | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [ticketPreviewFor, setTicketPreviewFor] = useState<number | null>(null);
  const [organizersMap, setOrganizersMap] = useState<Record<number, { name: string; image_url?: string }>>({});
  const [view, setView] = useState<'movies' | 'weddings'>('movies');

  useEffect(() => {
    async function fetchBookings() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data, error } = await supabase
          .from('bookings')
          .select(`
            *,
            cancellation_reason,
            screenings (
              *,
              movies (*)
            )
          `)
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (error) throw error;
        setBookings(data || []);
      } catch (error) {
        console.error('Error fetching bookings:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchBookings();
  }, []);

  useEffect(() => {
    async function fetchSeatIds() {
      if (!bookings.length) return;
      const bookingIds = bookings.map(b => b.id);
      const { data } = await supabase
        .from('booking_seats')
        .select('booking_id, seat_ids')
        .in('booking_id', bookingIds);
      const map: Record<number, string[]> = {};
      for (const row of ((data || []) as { booking_id: number; seat_ids: string[] }[])) {
        map[row.booking_id] = row.seat_ids || [];
      }
      setSeatIdsByBooking(map);
    }
    fetchSeatIds();
  }, [bookings]);

  // Fetch per-tier ticket breakdown for all bookings
  useEffect(() => {
    async function fetchTicketTypes() {
      if (!bookings.length) return;
      const bookingIds = bookings.map(b => b.id);
      const { data } = await supabase
        .from('booking_ticket_types')
        .select('booking_id, ticket_type, quantity')
        .in('booking_id', bookingIds);
      const map: Record<number, BookingTicketType[]> = {};
      for (const row of ((data || []) as { booking_id: number; ticket_type: string; quantity: number }[])) {
        if (!map[row.booking_id]) map[row.booking_id] = [];
        map[row.booking_id].push({ ticket_type: row.ticket_type as BookingTicketType['ticket_type'], quantity: row.quantity });
      }
      setTicketTypesByBooking(map);
    }
    fetchTicketTypes();
  }, [bookings]);

  useEffect(() => {
    (async () => {
      if (!bookings.length) { setOrganizersMap({}); return; }
      const ids = Array.from(new Set(bookings.map(b => b.screenings.movies.organizer_id).filter((x): x is number => !!x)));
      if (!ids.length) { setOrganizersMap({}); return; }
      const { data } = await supabase.from('organizers').select('id,name,image_url').in('id', ids);
      const map: Record<number, { name: string; image_url?: string }> = {};
      for (const row of ((data || []) as { id: number; name: string; image_url?: string }[])) map[row.id] = { name: row.name, image_url: row.image_url };
      setOrganizersMap(map);
    })();
  }, [bookings]);

  useEffect(() => {
    async function fetchQrs() {
      if (!bookings.length) return;
      const confirmedIds = bookings.filter(b => b.status === 'confirmed').map(b => b.id);
      if (!confirmedIds.length) {
        setQrValueByBooking({});
        return;
      }
      const { data } = await supabase
        .from('booking_qr_codes')
        .select('booking_id, iv_base64, cipher_base64, redeemed')
        .in('booking_id', confirmedIds);
      const map: Record<number, string> = {};
      const redeemedMap: Record<number, boolean> = {};
      for (const row of ((data || []) as { booking_id: number; iv_base64: string; cipher_base64: string; redeemed?: boolean }[])) {
        map[row.booking_id] = `v1:${row.iv_base64}:${row.cipher_base64}`;
        redeemedMap[row.booking_id] = !!row.redeemed;
      }
      setQrValueByBooking(map);
      setQrRedeemedByBooking(redeemedMap);
    }
    fetchQrs();
  }, [bookings]);

  // Real-time subscription for booking updates
  useEffect(() => {
    const channel = supabase
      .channel('public:bookings')
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'bookings' },
        (payload) => {
          setBookings((prev) =>
            prev.map((b) => (b.id === payload.new.id ? { ...b, ...payload.new } : b))
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUserEmail(session?.user?.email || '');
    });
  }, []);

  const handleCardClick = (id: number) => {
    setSelectedBookingId(prev => (prev === id ? null : id));
  };

  const cancelBooking = async (booking: ExtendedBooking) => {
    if (booking.status !== 'confirmed') {
      toast.error('Only confirmed bookings can be cancelled');
      return;
    }
    try {
      setCancelingId(booking.id);
      const { data: ticketRows, error: ticketErr } = await supabase
        .from('booking_ticket_types')
        .select('ticket_type, quantity')
        .eq('booking_id', booking.id);
      if (ticketErr) {
        toast.error('Failed to load ticket details');
        return;
      }
      const rows = (ticketRows || []) as { ticket_type: 'silver' | 'gold' | 'diamond'; quantity: number }[];
      for (const row of rows) {
        const seatColumn = row.ticket_type === 'silver' ? 'silver_booked' : row.ticket_type === 'gold' ? 'gold_booked' : 'diamond_booked';
        const { data: seatData, error: seatErr } = await supabase
          .from('seatssplitting')
          .select(seatColumn)
          .eq('screening_id', booking.screenings.id)
          .single();
        if (seatErr) {
          toast.error('Failed to fetch seat allocation');
          return;
        }
        const currentCount = ((seatData as Record<string, number>)?.[seatColumn] || 0);
        const updatedSeatCount = Math.max(0, currentCount - row.quantity);
        const { error: updateErr } = await supabase
          .from('seatssplitting')
          .update({ [seatColumn]: updatedSeatCount })
          .eq('screening_id', booking.screenings.id);
        if (updateErr) {
          toast.error('Failed to update seat allocation');
          return;
        }
      }
      const { error: cancelErr } = await supabase
        .from('bookings')
        .update({ status: 'cancelled' })
        .eq('id', booking.id);
      if (cancelErr) {
        toast.error('Failed to cancel booking');
        return;
      }
      await supabase
        .from('booking_seats')
        .delete()
        .eq('booking_id', booking.id);
      await supabase
        .from('booking_qr_codes')
        .update({ expires_at: new Date().toISOString(), redeemed: true })
        .eq('booking_id', booking.id);
      setBookings(prev => prev.map(b => (b.id === booking.id ? { ...b, status: 'cancelled' } : b)));
      toast.success('Booking cancelled');
      setSelectedBookingId(null);
    } finally {
      setCancelingId(null);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-75 z-50 overflow-y-auto">
        <div className="max-w-4xl w-full mx-auto my-8 bg-gray-800 dark:bg-white rounded-lg p-4 sm:p-6 text-white dark:text-gray-900 max-h-[85vh] overflow-y-auto">
          <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
            <h2 className="text-2xl font-bold text-white dark:text-gray-900">My Bookings</h2>

            {/* Tabs */}
            <div className="flex bg-gray-700 dark:bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setView('movies')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${view === 'movies'
                  ? 'bg-gray-600 dark:bg-white text-white dark:text-gray-900 shadow-sm'
                  : 'text-gray-300 dark:text-gray-500 hover:text-white dark:hover:text-gray-900'
                  }`}
              >
                Movie Tickets
              </button>
              <button
                onClick={() => setView('weddings')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${view === 'weddings'
                  ? 'bg-gray-600 dark:bg-white text-white dark:text-gray-900 shadow-sm'
                  : 'text-gray-300 dark:text-gray-500 hover:text-white dark:hover:text-gray-900'
                  }`}
              >
                Weddings
              </button>
            </div>

            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-600 transition-colors"
            >
              ×
            </button>
          </div>

          {view === 'weddings' ? (
            <WeddingBookings />
          ) : (
            /* Movie Bookings Content */
            <>
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-800 dark:border-gray-200 mx-auto"></div>
                  <p className="mt-4 text-gray-600 dark:text-gray-400">Loading your bookings...</p>
                </div>
              ) : bookings.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-600 dark:text-gray-400">No bookings found</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {bookings.map((booking) => {
                    const expanded = selectedBookingId === booking.id;
                    const bookingData = {
                      id: booking.id,
                      movie: booking.screenings.movies.title,
                      seats: booking.seats,
                      date: booking.screenings.start_time,
                      location: booking.screenings.location,
                      amount: booking.total_amount,
                      email: userEmail,
                      seat_ids: seatIdsByBooking[booking.id]
                    };
                    return (
                      <div
                        key={booking.id}
                        className={`bg-gray-800 text-white dark:bg-white dark:text-gray-900 rounded-lg p-4 hover:bg-gray-700 dark:hover:bg-gray-100 transition-colors cursor-pointer ${
                          booking.cancellation_reason === 'price_mismatch'
                            ? 'border-l-4 border-amber-400'
                            : ''
                        }`}
                        onClick={() => handleCardClick(booking.id)}
                      >
                        {/* Price mismatch notice — visible even when collapsed */}
                        {booking.cancellation_reason === 'price_mismatch' && (
                          <div className="flex items-start gap-2 bg-amber-500/15 border border-amber-400/40 rounded-lg px-3 py-2 mb-3">
                            <AlertTriangle className="h-4 w-4 text-amber-400 mt-0.5 shrink-0 animate-pulse" />
                            <div>
                              <p className="text-amber-300 dark:text-amber-700 text-sm font-semibold leading-tight">
                                Payment issue detected
                              </p>
                              <p className="text-amber-200/80 dark:text-amber-600 text-xs mt-0.5 leading-snug">
                                There was a pricing error with this booking. The organiser has been notified and will contact you shortly to re-book at the correct price. No charge was made.
                              </p>
                            </div>
                          </div>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <h3 className="text-xl font-semibold text-white dark:text-gray-900">
                              {booking.screenings.movies.title}
                            </h3>
                            <p className="text-white/90 dark:text-gray-900 mt-1">
                              {format(new Date(booking.screenings.start_time), 'PPP p')}
                            </p>
                            <p className="text-white/90 dark:text-gray-900">
                              Location: {booking.screenings.location}
                            </p>
                          </div>
                          <div className="md:text-right min-w-0">
                            <p className="text-white/90 dark:text-gray-900">
                              Seats: <span className="font-semibold">{booking.seats}</span>{seatIdsByBooking[booking.id]?.length ? (
                                <span className="ml-2 text-white/70 dark:text-gray-600 break-words">({seatIdsByBooking[booking.id].join(', ')})</span>
                              ) : null}
                            </p>
                            {/* Tier breakdown badges */}
                            {ticketTypesByBooking[booking.id]?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1 justify-end">
                                {ticketTypesByBooking[booking.id].map(({ ticket_type, quantity }) => (
                                  <span
                                    key={ticket_type}
                                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                                      ticket_type === 'silver' ? 'bg-gray-200 text-gray-700'
                                      : ticket_type === 'gold'   ? 'bg-yellow-200 text-yellow-800'
                                      : 'bg-blue-200 text-blue-800'
                                    }`}
                                  >
                                    {ticket_type === 'silver' ? '🥈' : ticket_type === 'gold' ? '🥇' : '💎'}
                                    {' '}{ticket_type.charAt(0).toUpperCase() + ticket_type.slice(1)} × {quantity}
                                  </span>
                                ))}
                              </div>
                            )}
                            <p className="text-white/90 dark:text-gray-900">
                              Total Paid:{' '}
                              <span className="font-semibold text-white dark:text-gray-900">
                                €{booking.total_amount.toFixed(2)}
                              </span>
                            </p>
                            <p className="text-white/90 dark:text-gray-900">
                              Status:{' '}
                              <span className={`capitalize font-semibold ${isExpiredPending(booking) ? 'text-red-400' :
                                booking.status === 'cancelled' ? 'text-red-500' :
                                  booking.status === 'confirmed' ? 'text-green-400' :
                                    'text-yellow-400'
                                }`}>
                                {isExpiredPending(booking) ? 'Timeout Expired' : booking.status}
                              </span>
                            </p>
                          </div>
                        </div>
                        {expanded && (
                          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                            <div className="flex justify-center">
                              {booking.status === 'confirmed' && qrValueByBooking[booking.id] ? (
                                <div className="bg-white dark:bg-white p-4 rounded-lg shadow-md">
                                  <QRCodeSVG value={qrValueByBooking[booking.id]} size={180} level="H" includeMargin={true} />
                                </div>
                              ) : (
                                <div className="text-white/80 dark:text-gray-900">QR unavailable</div>
                              )}
                            </div>
                            <div className="md:text-right flex md:justify-end">
                              <div className="flex flex-wrap gap-3 justify-end w-full">
                                {/* Expired Pending: Delete button */}
                                {isExpiredPending(booking) && (
                                  <button
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      setDeletingId(booking.id);
                                      try {
                                        // Release seats from seatssplitting if ticket types exist
                                        const { data: ticketRows } = await supabase
                                          .from('booking_ticket_types')
                                          .select('ticket_type, quantity')
                                          .eq('booking_id', booking.id);

                                        if (ticketRows && ticketRows.length > 0) {
                                          for (const row of ticketRows as { ticket_type: 'silver' | 'gold' | 'diamond'; quantity: number }[]) {
                                            const seatColumn = row.ticket_type === 'silver' ? 'silver_booked' :
                                              row.ticket_type === 'gold' ? 'gold_booked' : 'diamond_booked';

                                            // Get current count and decrement
                                            const { data: seatData } = await supabase
                                              .from('seatssplitting')
                                              .select(seatColumn)
                                              .eq('screening_id', booking.screenings.id)
                                              .single();

                                            if (seatData) {
                                              const currentCount = (seatData as Record<string, number>)[seatColumn] || 0;
                                              const updatedCount = Math.max(0, currentCount - row.quantity);

                                              await supabase
                                                .from('seatssplitting')
                                                .update({ [seatColumn]: updatedCount })
                                                .eq('screening_id', booking.screenings.id);
                                            }
                                          }
                                        }

                                        // Now delete the booking records
                                        await supabase.from('booking_seats').delete().eq('booking_id', booking.id);
                                        await supabase.from('booking_ticket_types').delete().eq('booking_id', booking.id);
                                        await supabase.from('bookings').delete().eq('id', booking.id);
                                        setBookings(prev => prev.filter(b => b.id !== booking.id));
                                        setDeletingId(null);
                                        setShowDeleteSuccess(true);
                                      } catch (err) {
                                        console.error('Failed to clear expired booking:', err);
                                        toast.error('Failed to clear expired booking');
                                        setDeletingId(null);
                                      }
                                    }}
                                    disabled={deletingId === booking.id}
                                    aria-label="Delete expired booking"
                                    className="px-4 py-2 rounded-md bg-red-600 hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white flex items-center gap-2"
                                  >
                                    {deletingId === booking.id ? (
                                      <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                        <span>Deleting...</span>
                                      </>
                                    ) : (
                                      <>
                                        <Trash2 className="h-4 w-4" />
                                        <span>Delete</span>
                                      </>
                                    )}
                                  </button>
                                )}

                                {/* Active Pending (not expired): Delete + Continue */}
                                {booking.status === 'pending' && !isExpiredPending(booking) && (
                                  <>
                                    <button
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        setDeletingId(booking.id);
                                        try {
                                          // Release seats from seatssplitting if ticket types exist
                                          const { data: ticketRows } = await supabase
                                            .from('booking_ticket_types')
                                            .select('ticket_type, quantity')
                                            .eq('booking_id', booking.id);

                                          if (ticketRows && ticketRows.length > 0) {
                                            for (const row of ticketRows as { ticket_type: 'silver' | 'gold' | 'diamond'; quantity: number }[]) {
                                              const seatColumn = row.ticket_type === 'silver' ? 'silver_booked' :
                                                row.ticket_type === 'gold' ? 'gold_booked' : 'diamond_booked';

                                              // Get current count and decrement
                                              const { data: seatData } = await supabase
                                                .from('seatssplitting')
                                                .select(seatColumn)
                                                .eq('screening_id', booking.screenings.id)
                                                .single();

                                              if (seatData) {
                                                const currentCount = (seatData as Record<string, number>)[seatColumn] || 0;
                                                const updatedCount = Math.max(0, currentCount - row.quantity);

                                                await supabase
                                                  .from('seatssplitting')
                                                  .update({ [seatColumn]: updatedCount })
                                                  .eq('screening_id', booking.screenings.id);
                                              }
                                            }
                                          }

                                          // Now delete the booking records
                                          await supabase.from('booking_seats').delete().eq('booking_id', booking.id);
                                          await supabase.from('booking_ticket_types').delete().eq('booking_id', booking.id);
                                          await supabase.from('bookings').delete().eq('id', booking.id);
                                          setBookings(prev => prev.filter(b => b.id !== booking.id));
                                          setDeletingId(null);
                                          setShowDeleteSuccess(true);
                                        } catch (err) {
                                          console.error('Failed to delete pending booking:', err);
                                          toast.error('Failed to delete pending booking');
                                          setDeletingId(null);
                                        }
                                      }}
                                      disabled={deletingId === booking.id}
                                      aria-label="Delete pending booking"
                                      className="px-4 py-2 rounded-md bg-red-600 hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white flex items-center gap-2"
                                    >
                                      {deletingId === booking.id ? (
                                        <>
                                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                          <span>Deleting...</span>
                                        </>
                                      ) : (
                                        <>
                                          <Trash2 className="h-4 w-4" />
                                          <span>Delete</span>
                                        </>
                                      )}
                                    </button>
                                    <button
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        // Call the continue callback with booking info
                                        if (onContinuePending) {
                                          setContinuingId(booking.id);
                                          try {
                                            const seatIds = seatIdsByBooking[booking.id] || [];
                                            await onContinuePending(
                                              booking.screenings.movies,
                                              booking.screenings.id,
                                              booking.id,
                                              seatIds
                                            );
                                            onClose();
                                          } catch (err) {
                                            console.error('Failed to continue booking:', err);
                                            toast.error('Failed to continue booking');
                                          } finally {
                                            setContinuingId(null);
                                          }
                                        } else {
                                          toast.success(`Please find "${booking.screenings.movies.title}" on the home page to complete your booking.`);
                                          onClose();
                                        }
                                      }}
                                      disabled={continuingId === booking.id}
                                      aria-label="Continue booking"
                                      className="px-4 py-2 rounded-md bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white flex items-center gap-2"
                                    >
                                      {continuingId === booking.id ? (
                                        <>
                                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                          <span>Loading...</span>
                                        </>
                                      ) : (
                                        <>
                                          <RefreshCw className="h-4 w-4" />
                                          <span>Continue</span>
                                        </>
                                      )}
                                    </button>
                                  </>
                                )}

                                {/* Confirmed: Cancel, Preview, Download, Feedback */}
                                {booking.status === 'confirmed' && (
                                  <>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (qrRedeemedByBooking[booking.id]) { toast.error('Ticket already redeemed — cannot cancel'); return; }
                                        toast.custom((t) => (
                                          <div className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-lg shadow-lg p-4 w-full max-w-sm">
                                            <div className="flex items-start gap-3">
                                              <AlertTriangle className="h-5 w-5 text-yellow-400" />
                                              <div className="flex-1">
                                                <p className="font-semibold">Cancel this booking?</p>
                                                <p className="text-sm text-gray-700 dark:text-gray-300">This will release seats and invalidate the ticket.</p>
                                              </div>
                                            </div>
                                            <div className="mt-3 flex gap-3 justify-end">
                                              <button onClick={() => { toast.dismiss(t.id); }} className="px-3 py-2 bg-gray-200 text-gray-900 hover:bg-gray-300 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600 rounded">Keep Booking</button>
                                              <button onClick={() => { toast.dismiss(t.id); cancelBooking(booking); }} className="px-3 py-2 bg-red-600 hover:bg-red-700 rounded flex items-center gap-2"><ShieldCheck className="h-4 w-4" />Confirm Cancel</button>
                                            </div>
                                          </div>
                                        ));
                                      }}
                                      disabled={cancelingId === booking.id || qrRedeemedByBooking[booking.id]}
                                      aria-label="Cancel booking"
                                      className={`px-4 py-2 rounded-md ${cancelingId === booking.id || qrRedeemedByBooking[booking.id] ? 'bg-gray-500 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'} text-white flex items-center justify-center`}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); setTicketPreviewFor(booking.id); }}
                                      aria-label="Preview ticket"
                                      className="px-4 py-2 rounded-md bg-gray-800 text-white hover:bg-gray-700 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 flex items-center gap-2"
                                    >
                                      <Eye className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        try {
                                          const { data: { session } } = await supabase.auth.getSession();
                                          const token = session?.access_token || '';
                                          const urlBase = import.meta.env.VITE_SUPABASE_URL;
                                          const resp = await fetch(`${urlBase}/functions/v1/generate-ticket-pdf`, {
                                            method: 'POST',
                                            headers: {
                                              'Authorization': `Bearer ${token}`,
                                              'Content-Type': 'application/json'
                                            },
                                            body: JSON.stringify({ booking_id: booking.id })
                                          });
                                          if (!resp.ok) {
                                            toast.error('Failed to generate PDF');
                                            return;
                                          }
                                          const blob = await resp.blob();
                                          const url = URL.createObjectURL(blob);
                                          const a = document.createElement('a');
                                          a.href = url;
                                          a.download = `booking_${booking.id}.pdf`;
                                          document.body.appendChild(a);
                                          a.click();
                                          a.remove();
                                          URL.revokeObjectURL(url);
                                        } catch {
                                          toast.error('Failed to generate PDF');
                                        }
                                      }}
                                      aria-label="Download ticket"
                                      className="px-4 py-2 rounded-md bg-gray-800 text-white hover:bg-gray-700 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 flex items-center justify-center"
                                    >
                                      <Download className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setFeedbackSubject('Feedback about booking');
                                        setFeedbackMessage('');
                                        setFeedbackOpenFor(booking.id);
                                      }}
                                      aria-label="Send feedback"
                                      className="px-4 py-2 rounded-md bg-gray-800 text-white hover:bg-gray-700 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 flex items-center justify-center"
                                    >
                                      <Mail className="h-4 w-4" />
                                    </button>
                                  </>
                                )}

                                {/* Cancelled: No action buttons */}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2 rounded-md bg-gray-800 text-white hover:bg-gray-700 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div >

      {/* Delete Success Modal */}
      {
        showDeleteSuccess && (
          <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-900 mb-4">
                  <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Booking Deleted!</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                  Your pending booking has been successfully deleted and the seats have been released.
                </p>
                <button
                  onClick={() => {
                    setShowDeleteSuccess(false);
                    onClose();
                  }}
                  className="px-4 py-2 rounded-md bg-gray-800 text-white hover:bg-gray-700 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )
      }
      {
        feedbackOpenFor && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-lg">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Send Feedback</h3>
              <div className="space-y-3">
                <input
                  placeholder="Subject"
                  value={feedbackSubject}
                  onChange={e => setFeedbackSubject(e.target.value)}
                  className="w-full px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white"
                />
                <textarea
                  placeholder="Your message"
                  value={feedbackMessage}
                  onChange={e => setFeedbackMessage(e.target.value)}
                  rows={5}
                  className="w-full px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white"
                />
                <div className="flex gap-3 justify-end">
                  <button onClick={() => setFeedbackOpenFor(null)} className="px-4 py-2 bg-gray-200 text-gray-900 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 rounded-lg">Cancel</button>
                  <button
                    onClick={async () => {
                      if (!feedbackMessage.trim()) {
                        toast.error('Enter a message');
                        return;
                      }
                      setSendingFeedback(true);
                      try {
                        const { data, error } = await supabase.functions.invoke('send-feedback', { body: { booking_id: feedbackOpenFor, subject: feedbackSubject, message: feedbackMessage } });
                        if (error) {
                          toast.error('Failed to send');
                        } else {
                          const warning = (data as { warning?: string } | null)?.warning;
                          if (warning === 'no_contact_email') {
                            toast.success('Feedback recorded; organizer email missing');
                          } else if (warning === 'email_not_configured') {
                            toast.success('Feedback recorded; email not configured');
                          } else {
                            toast.success('Feedback sent');
                          }
                          setFeedbackOpenFor(null);
                        }
                      } catch {
                        toast.error('Failed to send');
                      } finally {
                        setSendingFeedback(false);
                      }
                    }}
                    disabled={sendingFeedback}
                    className="px-4 py-2 rounded-lg bg-gray-800 text-white dark:bg-white dark:text-gray-900 disabled:opacity-50"
                  >
                    {sendingFeedback ? 'Sending...' : 'Send'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }
      {
        pdfPreviewFor && pdfUrl && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 w-full max-w-3xl h-[80vh] flex flex-col">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Ticket Preview</h3>
                <button
                  onClick={() => {
                    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
                    setPdfUrl(null);
                    setPdfPreviewFor(null);
                  }}
                  className="text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  ×
                </button>
              </div>
              <div className="flex-1 bg-gray-100 dark:bg-gray-900">
                <object data={pdfUrl} type="application/pdf" className="w-full h-full">
                  <p className="text-gray-700 dark:text-gray-300 p-4">Cannot display PDF. <a href={pdfUrl} target="_blank" rel="noreferrer" className="text-indigo-600 dark:text-indigo-400">Open in new tab</a></p>
                </object>
              </div>
              <div className="mt-3 flex gap-3 justify-end">
                <button
                  onClick={() => {
                    // open in new tab for better viewer controls
                    if (pdfUrl) window.open(pdfUrl, '_blank');
                  }}
                  className="px-4 py-2 rounded-lg bg-gray-800 text-white hover:bg-gray-700 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
                >
                  Open in Tab
                </button>
                <button
                  onClick={() => {
                    if (!pdfUrl) return;
                    const a = document.createElement('a');
                    a.href = pdfUrl;
                    a.download = `booking_${pdfPreviewFor}.pdf`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                  }}
                  className="px-4 py-2 rounded-lg bg-gray-800 text-white hover:bg-gray-700 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
                >
                  Download
                </button>
              </div>
            </div>
          </div>
        )
      }
      {
        ticketPreviewFor && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl w-full max-w-lg md:max-w-2xl max-h-[85vh] overflow-y-auto scrollbar-hide mx-0 relative">
              <div className="flex justify-between items-center px-4 pt-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Ticket Preview</h3>
                <button onClick={() => setTicketPreviewFor(null)} className="text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200">×</button>
              </div>
              <div className="bg-gradient-to-r from-purple-600 to-blue-600 p-6 text-white relative overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 bg-white opacity-10 rounded-full -mr-12 -mt-12"></div>
                <div className="absolute bottom-0 left-0 w-20 h-20 bg-white opacity-10 rounded-full -ml-10 -mb-10"></div>
                <div className="relative z-10">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center gap-2">
                      <div className="bg-white bg-opacity-20 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold">CONFIRMED</div>
                      {(() => {
                        const rid = ticketPreviewFor;
                        const redeemed = rid ? qrRedeemedByBooking[rid] : false;
                        return (
                          <div className={`px-3 py-1 rounded-full text-xs font-semibold ${redeemed ? 'bg-yellow-500 text-black' : 'bg-green-600 text-white'}`}>
                            {redeemed ? 'Redeemed' : 'Not Redeemed'}
                          </div>
                        );
                      })()}
                    </div>
                    <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center"><span className="text-2xl">🎬</span></div>
                  </div>
                  {(() => {
                    const b = bookings.find(x => x.id === ticketPreviewFor)!;
                    const movieName = b.screenings.movies.title;
                    const org = organizersMap[b.screenings.movies.organizer_id || -1];
                    const organizerName = org?.name || '';
                    const organizerImg = org?.image_url;
                    const appName = typeof document !== 'undefined' ? document.title : 'Eventador';
                    return (
                      <div className="flex flex-col items-center gap-2 w-full">
                        <h1 className="text-2xl font-bold mb-1 bg-gradient-to-r from-amber-500 to-orange-600 dark:from-yellow-300 dark:to-amber-600 bg-clip-text text-transparent">{movieName}</h1>
                        <div className="flex items-center gap-3">
                          {organizerImg && (
                            <img src={organizerImg} alt={organizerName} className="h-8 w-8 rounded inline-block align-middle" />
                          )}
                          <span className="urbanist-brand text-xl font-bold bg-gradient-to-r from-black to-gray-700 bg-clip-text text-transparent">{appName}</span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
              <div className="relative h-6 bg-white">
                <svg className="absolute top-0 left-0 right-0 w-full h-6" preserveAspectRatio="none" viewBox="0 0 400 32">
                  <path d="M0,0 Q10,16 20,0 T40,0 T60,0 T80,0 T100,0 T120,0 T140,0 T160,0 T180,0 T200,0 T220,0 T240,0 T260,0 T280,0 T300,0 T320,0 T340,0 T360,0 T380,0 T400,0 L400,32 L0,32 Z" fill="white" stroke="none" />
                </svg>
              </div>
              <div className="p-6 space-y-5 bg-white">
                {(() => {
                  const b = bookings.find(x => x.id === ticketPreviewFor)!;
                  if (b.status === 'cancelled') {
                    return (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="rotate-12 text-red-500/70 text-4xl md:text-6xl font-extrabold tracking-widest bg-white/20 px-6 py-3 rounded-xl border border-red-500/40">
                          CANCELLED BOOKING
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
                {(() => {
                  const b = bookings.find(x => x.id === ticketPreviewFor)!;
                  const seatList = (seatIdsByBooking[b.id] || []).join(', ');
                  const amount = b.total_amount;
                  const showDate = format(new Date(b.screenings.start_time), 'PPP');
                  const showTime = format(new Date(b.screenings.start_time), 'p');
                  const location = b.screenings.location;
                  const imgUrl = (b as any).screenings?.movies?.image_url as string | undefined;
                  const customerName = (b as any).customer_name || (userEmail || '').split('@')[0];
                  const org = organizersMap[b.screenings.movies.organizer_id || -1];
                  const organizerName = org?.name || '';
                  const organizerImg = org?.image_url;
                  return (
                    <>
                      <div className="flex items-center justify-center">
                        <div className="w-full sm:w-64 h-36 bg-gray-200 rounded-xl overflow-hidden shadow">
                          {imgUrl ? (
                            <img src={imgUrl} alt={b.screenings.movies.title} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center text-gray-600">No image</div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <span className="text-xl">📍</span>
                        </div>
                        <div className="flex-1">
                          <p className="text-xs text-gray-500 font-medium mb-1">LOCATION</p>
                          <p className="text-gray-900 font-semibold">{location}</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <span className="text-xl">📅</span>
                        </div>
                        <div className="flex-1">
                          <p className="text-xs text-gray-500 font-medium mb-1">DATE & TIME</p>
                          <p className="text-gray-900 font-semibold">{showDate}</p>
                          <p className="text-gray-600 text-sm">{showTime}</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <span className="text-xl">💺</span>
                        </div>
                        <div className="flex-1">
                          <p className="text-xs text-gray-500 font-medium mb-1">SEATS</p>
                          <p className="text-gray-900 font-semibold text-lg">{seatList || b.seats}</p>
                          {/* Tier breakdown in preview */}
                          {ticketTypesByBooking[b.id]?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {ticketTypesByBooking[b.id].map(({ ticket_type, quantity }) => (
                                <span
                                  key={ticket_type}
                                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                                    ticket_type === 'silver' ? 'bg-gray-100 text-gray-700'
                                    : ticket_type === 'gold'   ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-blue-100 text-blue-800'
                                  }`}
                                >
                                  {ticket_type === 'silver' ? '🥈' : ticket_type === 'gold' ? '🥇' : '💎'}
                                  {' '}{ticket_type.charAt(0).toUpperCase() + ticket_type.slice(1)} × {quantity}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <span className="text-xl">💳</span>
                        </div>
                        <div className="flex-1">
                          <p className="text-xs text-gray-500 font-medium mb-1">PAYMENT</p>
                          <p className="text-gray-900 font-semibold">€{amount.toFixed(2)}</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <span className="text-xl">👤</span>
                        </div>
                        <div className="flex-1">
                          <p className="text-xs text-gray-500 font-medium mb-1">CUSTOMER</p>
                          <p className="text-gray-900 font-semibold">{customerName}</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <span className="text-xl">🏟</span>
                        </div>
                        <div className="flex-1">
                          <p className="text-xs text-gray-500 font-medium mb-1">ORGANIZER</p>
                          <div className="flex items-center gap-2">
                            {organizerImg && (
                              <img src={organizerImg} alt={organizerName} className="h-6 w-6 rounded inline-block align-middle" />
                            )}
                            <span className="urbanist-brand text-sm font-semibold text-black">{organizerName}</span>
                          </div>
                        </div>
                      </div>
                      <div className="border-t border-dashed border-gray-300 pt-5 mt-5">
                        <div className="flex flex-col items-center">
                          <div className="w-40 h-40 bg-gradient-to-br from-purple-100 to-blue-100 rounded-xl flex items-center justify-center mb-3 shadow-inner">
                            {qrValueByBooking[b.id] ? (
                              <QRCodeSVG value={qrValueByBooking[b.id]} size={140} level="H" includeMargin={false} />
                            ) : (
                              <span className="text-gray-700">QR unavailable</span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 text-center font-medium">Scan this code at the entrance</p>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
              <div className="bg-white px-6 pb-6">
                <div className="flex items-center justify-center pt-4 border-t border-gray-200">
                  <div className="text-center">
                    <span className="urbanist-brand text-xl font-bold bg-gradient-to-r from-black to-gray-700 bg-clip-text text-transparent">Eventador</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      }
      {
        pdfLoading && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center">
            <div className="bg-gray-800 rounded-lg p-6 text-white">Generating PDF...</div>
          </div>
        )
      }
    </>
  );
}
